import React from 'react'
import { useReactiveVar } from '@apollo/client'
import humanId from 'human-id'

import { memoryCurrentGame } from './lib'
import { addGameToStorage, returnGameFromStorage } from './logic'
import { generateSudoku } from './lib/sudoku'

import { Button, Dropdown, Modal } from 'antd'
import { PlusCircleOutlined } from '@ant-design/icons'

const DIFFICULTIES = [
  { key: 'easy', label: 'Easy', clues: 45 },
  { key: 'intermediate', label: 'Intermediate', clues: 40 },
  { key: 'hard', label: 'Hard', clues: 35 },
  { key: 'hardest', label: 'Hardest', clues: 31 },
  { key: 'expert', label: 'Expert', clues: 28 },
  { key: 'absurd', label: 'Absurd', clues: 26 }
]

// True once the user has placed/cleared at least one cell (any change from the
// original clue layout). A freshly-created game that was never touched counts
// as "not moved" so we don't nag on it.
const hasUserMoved = game => {
  if (!game || !Array.isArray(game.puzzle)) return false
  const original = game.originalPuzzleFormatted
    ? game.originalPuzzleFormatted.flat().map(cell => (cell.display == null ? null : cell.display - 1))
    : null
  if (!original) return true // can't tell — be safe and confirm
  return game.puzzle.some((value, i) => value !== original[i])
}

const NewGameButton = () => {
  const [modal, contextHolder] = Modal.useModal()
  const currentGameId = useReactiveVar(memoryCurrentGame)

  const createNewGame = difficulty => {
    const newSudoku = generateSudoku(difficulty.clues)

    const gameId = humanId()
    const createdAt = new Date()

    // The gate's shim, not a library: `window.siteTrack` does not EXIST until the visitor
    // has said yes, so a refused visit has no way to send this event at all.
    window.siteTrack?.('new_game_button', { label: difficulty.key })

    const { puzzleFormatted: originalPuzzleFormatted } = newSudoku

    const newGame = {
      gameId,
      createdAt,
      difficulty: difficulty.label,
      // a new game starts at zero mistakes — the tally lives with the game so it survives a
      // reload and shows up in the saved list
      mistakeCount: 0,
      ...newSudoku,
      originalPuzzleFormatted
    }

    addGameToStorage(newGame)
    memoryCurrentGame(gameId)
  }

  const confirmNewGame = difficulty => {
    if (!currentGameId) {
      createNewGame(difficulty)
      return
    }

    const currentGame = returnGameFromStorage(currentGameId)
    if (!hasUserMoved(currentGame)) {
      createNewGame(difficulty)
      return
    }

    modal.confirm({
      title: `Start a new ${difficulty.label} game?`,
      content: 'No worries — your current game is already saved, so you can pick it back up anytime from Saved games.',
      okText: 'Start new game',
      cancelText: 'Cancel',
      onOk: () => createNewGame(difficulty)
    })
  }

  const items = DIFFICULTIES.map(d => ({ key: d.key, label: d.label }))

  return (
    <div style={{ textAlign: 'center' }}>
      {contextHolder}
      <Dropdown
        menu={{
          items,
          onClick: ({ key }) => {
            const difficulty = DIFFICULTIES.find(d => d.key === key)
            confirmNewGame(difficulty)
          }
        }}
        trigger={['click']}
      >
        <Button
          type='primary'
          size='large'
          shape='round'
          icon={<PlusCircleOutlined />}
        >
          New game
        </Button>
      </Dropdown>
    </div>
  )
}

export default NewGameButton
